import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Loader2, Sparkles, BookOpen, Brain, Heart, AlertCircle, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { APP_VERSION, APP_RELEASE_TAG } from "@/lib/version";
import { LoginErrorBoundary } from "@/components/LoginErrorBoundary";

import { ROLE_HOME } from "@/lib/navConfig";
import { LOGO_TRANSPARENT } from "@/lib/logoData";
import { OWL_CELEBRATION, OWL_CLASSIC } from "@/lib/avatars";

// Limpieza silenciosa: borra storage local sin warning visible.
// Mantiene el avatar cacheado para no parpadear, y el token de auth
// para que el login Bearer-fallback no se pierda (Safari ITP).
function silentCacheClear() {
  try {
    const KEEP = new Set(["neuromath_access_token"]);
    Object.keys(localStorage).forEach((k) => {
      if (!KEEP.has(k)) localStorage.removeItem(k);
    });
    sessionStorage.clear();
    if (caches && caches.keys) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  } catch (_) { /* ignore */ }
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState({ email: "", password: "" });
  const [generalError, setGeneralError] = useState("");
  const [locked, setLocked] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);

  useEffect(() => {
    if (user && user.must_change_password) {
      navigate("/cambiar-clave-inicial", { replace: true });
    } else if (user && ROLE_HOME[user.role]) {
      navigate(ROLE_HOME[user.role], { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError({ email: "", password: "" });
    setGeneralError("");
    silentCacheClear(); // requerimiento: limpieza silenciosa antes del intento
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      if (res.user.must_change_password) {
        navigate("/cambiar-clave-inicial", { replace: true });
      } else {
        navigate(ROLE_HOME[res.user.role] || "/", { replace: true });
      }
      return;
    }
    // res.detail puede ser un dict del backend con {code, field, message}
    const detail = res.detail || {};
    if (detail.code === "EMAIL_NOT_FOUND") {
      setFieldError({ email: detail.message, password: "" });
    } else if (detail.code === "WRONG_PASSWORD") {
      setFieldError({ email: "", password: detail.message });
    } else if (detail.code === "ACCOUNT_LOCKED") {
      setLocked(true);
      setGeneralError(detail.message);
      // Auto-envío del link de recuperación al bloquearse la cuenta.
      // El botón manual queda como fallback por si el correo no llega.
      if (email && !recoverySent) {
        requestRecovery({ silent: true });
      }
    } else {
      setGeneralError(res.error || "No fue posible iniciar sesión.");
    }
  };

  const requestRecovery = async ({ silent = false } = {}) => {
    if (!email) {
      if (!silent) toast.error("Ingresa tu correo arriba primero.");
      return;
    }
    try {
      await api.post("/auth/request-unlock", { email, origin: window.location.origin });
      setRecoverySent(true);
      toast.success("Link de recuperación enviado · Revisa tu correo");
    } catch (_) {
      if (!silent) toast.error("No fue posible solicitar el link. Intenta más tarde.");
    }
  };

  return (
    <div className="min-h-screen neo-login flex items-center justify-center p-4 sm:p-8 notranslate" translate="no" data-testid="login-page">
      {/* Fondo neomórfico: formas suaves en relieve (paleta Manual de Marca) */}
      <div className="jasq-blob bg-[#FFCC28]" style={{ width: 300, height: 300, top: -90, right: -70, opacity: 0.35 }} />
      <div className="jasq-blob bg-[#A43790]" style={{ width: 240, height: 240, bottom: -70, left: -50, opacity: 0.25, animationDelay: "1.5s" }} />
      <div className="jasq-blob bg-[#05A859]" style={{ width: 200, height: 200, top: "45%", left: "52%", opacity: 0.2, animationDelay: "3s" }} />

      <div className="relative z-10 w-full max-w-7xl grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <section className="lg:col-span-7 flex flex-col items-center lg:items-start gap-6 text-center lg:text-left">
          {/* Logotipo grande, sin fondo */}
          <div className="w-full" data-testid="login-logo-card">
            <img
              src={LOGO_TRANSPARENT}
              alt="NEUROMATEMÁTICAS JASQ"
              data-testid="login-logo"
              draggable={false}
              className="select-none mx-auto lg:mx-0 h-auto"
              style={{ width: "min(100%, 720px)" }}
            />
          </div>

          <div className="flex items-end gap-4 sm:gap-6 w-full justify-center lg:justify-start">
            <img
              src={OWL_CELEBRATION}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = OWL_CLASSIC; }}
              alt="Zarek, la mascota de JASQ"
              data-testid="login-zarek"
              draggable={false}
              className="w-28 h-28 sm:w-40 sm:h-40 object-contain shrink-0 drop-shadow-xl"
            />
            <div className="rounded-3xl bg-white/70 border border-white p-4 sm:p-5 relative text-left max-w-md" data-testid="login-zarek-bubble">
              <p className="font-brand text-xl sm:text-2xl text-[#A43790] leading-tight">¡Hola! Soy Zarek 🦉</p>
              <p className="text-sm sm:text-base text-[#4C5478] leading-snug mt-1">
                Te acompaño a aprender matemáticas <b className="text-[#A43790]">sintiendo</b>, <b className="text-[#315197]">pensando</b> y <b className="text-[#05A859]">creciendo</b>.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 w-full max-w-xl">
            <div className="rounded-2xl bg-white/70 border border-white p-3 sm:p-4 text-center">
              <Brain size={22} className="mx-auto mb-2 text-[#A43790]" />
              <p className="font-bold text-xs sm:text-sm text-[#1F2547]">Diagnóstico integral</p>
            </div>
            <div className="rounded-2xl bg-white/70 border border-white p-3 sm:p-4 text-center">
              <Heart size={22} className="mx-auto mb-2 text-[#F2B705]" />
              <p className="font-bold text-xs sm:text-sm text-[#1F2547]">Sin miedo a fallar</p>
            </div>
            <div className="rounded-2xl bg-white/70 border border-white p-3 sm:p-4 text-center">
              <BookOpen size={22} className="mx-auto mb-2 text-[#05A859]" />
              <p className="font-bold text-xs sm:text-sm text-[#1F2547]">Tu propia ruta</p>
            </div>
          </div>
        </section>

        <section className="lg:col-span-5 w-full max-w-md mx-auto lg:max-w-none">
          <LoginErrorBoundary>
          <form onSubmit={handleSubmit} className="neo-card p-7 sm:p-9 space-y-5" data-testid="login-form" autoComplete="on" noValidate>
            <div className="flex items-center gap-2">
              <span className="w-11 h-11 rounded-full neo-flat flex items-center justify-center text-[#A43790]">
                <Sparkles size={18} />
              </span>
              <div>
                <h2 className="font-brand text-2xl text-[#1F2547]">Inicia sesión</h2>
                <p className="text-xs text-[#6E7591]">Bienvenido/a a NEUROMATEMÁTICAS JASQ®</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-bold text-[#4C5478] tracking-wide uppercase">Correo</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591] pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFieldError((f) => ({ ...f, email: "" })); }}
                  placeholder="tu@correo.com"
                  className={`neo-input w-full pl-10 pr-4 py-3 ${fieldError.email ? "ring-2 ring-[#EF4444]" : ""} font-medium text-[#1F2547] placeholder:text-[#A6ACC1]`}
                  required
                  autoComplete="username"
                  data-testid="login-email"
                  data-lpignore="true"
                  data-1p-ignore
                  data-bwignore="true"
                />
              </div>
              {fieldError.email && (
                <p className="text-xs text-[#EF4444] font-bold flex items-center gap-1 mt-1" data-testid="login-error-email">
                  <AlertCircle size={12} /> {fieldError.email}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-xs font-bold text-[#4C5478] tracking-wide uppercase">Contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591] pointer-events-none" />
                <input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldError((f) => ({ ...f, password: "" })); }}
                  placeholder="••••••••"
                  className={`neo-input w-full pl-10 pr-12 py-3 ${fieldError.password ? "ring-2 ring-[#EF4444]" : ""} font-medium text-[#1F2547] placeholder:text-[#A6ACC1]`}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  data-lpignore="true"
                  data-1p-ignore
                  data-bwignore="true"
                />
                <button type="button" onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591] hover:text-[#1F2547]"
                  tabIndex={-1} aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                >{showPwd ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
              {fieldError.password && (
                <p className="text-xs text-[#EF4444] font-bold flex items-center gap-1 mt-1" data-testid="login-error-password">
                  <AlertCircle size={12} /> {fieldError.password}
                </p>
              )}
            </div>

            {generalError && (
              <div className="text-sm rounded-xl bg-[#FFE9F0] border border-[#FAD0DE] text-[#A52258] px-3 py-2 flex items-start gap-2" data-testid="login-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{generalError}</span>
              </div>
            )}

            <button type="submit" disabled={loading}
              data-testid="login-submit"
              className="jasq-btn-primary w-full justify-center inline-flex items-center gap-2"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={16} />}
              <span>{loading ? "Entrando…" : "Entrar"}</span>
            </button>

            {locked && (
              <div className="pt-3 border-t border-[#E1E5F0] space-y-2" data-testid="login-locked-block">
                <p className="text-xs font-bold text-[#1F2547]">Tu cuenta está bloqueada</p>
                {recoverySent ? (
                  <div className="text-xs rounded-xl bg-[#E7F4E9] border border-[#BFE2C7] text-[#2A7A36] px-3 py-2 flex items-start gap-2" data-testid="login-recovery-sent">
                    <Mail size={14} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="font-extrabold">Link de recuperación enviado</p>
                      <p className="opacity-90">Revisa tu correo (<b>{email}</b>) y sigue las instrucciones para restablecer tu contraseña.</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[#6E7591]">
                    Estamos enviando un link a tu correo para que puedas restablecer la contraseña.
                  </p>
                )}
                <button type="button" onClick={() => requestRecovery()}
                  data-testid="login-request-recovery"
                  className="jasq-btn-ghost w-full justify-center inline-flex items-center gap-2 text-xs"
                  title="Solo si no recibiste el correo automáticamente"
                >
                  <Mail size={13} />
                  {recoverySent ? "Reenviar link de recuperación" : "Enviar link manualmente"}
                </button>
              </div>
            )}
          </form>
          </LoginErrorBoundary>
          <p
            className="mt-6 text-center text-[10px] font-bold tracking-wider uppercase text-[#A6ACC1]"
            data-testid="login-version"
          >
            v{APP_VERSION} · {APP_RELEASE_TAG}
          </p>
        </section>
      </div>
    </div>
  );
}
